import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { garageInfo } from '../data/mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConfirmation(true);
  };

  const confirmSend = () => {
    // Préparer le corps de l'email
    const emailBody = `Nom: ${formData.name}
Email: ${formData.email}
Téléphone: ${formData.phone || 'Non renseigné'}

Message:
${formData.message}`;

    const emailSubject = `Contact site web - ${formData.name}`;
    
    // Créer le lien mailto
    const mailtoLink = `mailto:${garageInfo.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Ouvrir l'application email
    window.location.href = mailtoLink;
    
    // Réinitialiser le formulaire et fermer la confirmation
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: ''
    });
    setShowConfirmation(false);
  };

  const cancelSend = () => {
    setShowConfirmation(false);
  };

  return (
    <section id="contact" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Contact
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Contactez-Nous
          </h2>
          <p className="text-lg text-slate-600">
            Notre équipe est à votre disposition pour répondre à vos questions
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Nos Coordonnées
            </h3>

            <div className="space-y-6 mb-8">
              {/* Address */}
              <div className="flex items-start gap-4 bg-white rounded-xl p-4 shadow-sm">
                <div className="bg-sky-100 rounded-lg p-3">
                  <MapPin className="text-sky-600" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Adresse</h4>
                  <p className="text-slate-600">{garageInfo.address}</p>
                  <p className="text-slate-600">{garageInfo.city}</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4 bg-white rounded-xl p-4 shadow-sm">
                <div className="bg-sky-100 rounded-lg p-3">
                  <Phone className="text-sky-600" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Téléphone</h4>
                  <a href={`tel:${garageInfo.phoneLink}`} className="text-sky-600 hover:text-sky-700 font-medium">
                    {garageInfo.phone}
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4 bg-white rounded-xl p-4 shadow-sm">
                <div className="bg-sky-100 rounded-lg p-3">
                  <Mail className="text-sky-600" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Email</h4>
                  <a href={`mailto:${garageInfo.email}`} className="text-sky-600 hover:text-sky-700 font-medium">
                    {garageInfo.email}
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start gap-4 bg-white rounded-xl p-4 shadow-sm">
                <div className="bg-sky-100 rounded-lg p-3">
                  <Clock className="text-sky-600" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Horaires</h4>
                  {Object.entries(garageInfo.hours).map(([day, hours]) => (
                    <div key={day} className="flex justify-between gap-8 text-sm mb-1">
                      <span className="text-slate-700 font-medium">{day}</span>
                      <span className="text-slate-600">{hours}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden shadow-lg h-64 relative group cursor-pointer">
              <iframe
                src="https://www.google.com/maps?q=Garage+1225+Sarl,Rue+François+Jacquier+17,1225+Chêne-Bourg,Switzerland&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Garage 1225 Sàrl - Chêne-Bourg"
              ></iframe>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Garage+1225+Sarl+Rue+François+Jacquier+17+1225+Chêne-Bourg+Switzerland"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute inset-0 z-10 opacity-0 hover:opacity-100 bg-black/20 flex items-center justify-center transition-opacity"
              >
                <span className="bg-sky-600 text-white px-6 py-3 rounded-lg font-semibold shadow-lg">
                  Ouvrir dans Google Maps
                </span>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <div className="bg-gradient-to-br from-white to-sky-50 rounded-2xl p-8 shadow-sm border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">
                Envoyez-nous un Message
              </h3>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 outline-none transition-all"
                    placeholder="Votre nom"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 outline-none transition-all"
                    placeholder="votre@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 outline-none transition-all"
                    placeholder="Votre numéro"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-200 outline-none transition-all resize-none"
                    placeholder="Décrivez votre besoin..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-sky-500 to-sky-600 text-white px-6 py-4 rounded-lg font-semibold hover:from-sky-600 hover:to-sky-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <Send size={20} />
                  Envoyer le Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Confirmer l'envoi
            </h3>
            <p className="text-slate-600 mb-6">
              Votre application de messagerie va s'ouvrir avec votre message prérempli. 
              Vous pourrez le modifier avant de l'envoyer.
            </p>
            <div className="flex gap-4">
              <button
                onClick={cancelSend}
                className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-all"
              >
                Annuler
              </button>
              <button
                onClick={confirmSend}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-sky-500 to-sky-600 text-white rounded-lg font-semibold hover:from-sky-600 hover:to-sky-700 transition-all shadow-lg"
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Contact;
